import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, FileSpreadsheet, BookOpen, Download, Database, Link2, Library, FileDown, Info, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SessionsBulkCreateDialog } from "@/components/attendance/SessionsBulkCreateDialog";
import { downloadSessionLibraryTemplate } from "@/lib/sessionLibraryTemplate";
import { findDuplicateSessionNos, keyOf as sessionKeyOf } from "@/lib/sessionLibraryValidation";

type ModuleOpt = { module_code: string; module_name: string | null };

const ALL = "__all__";

async function fetchAllChunks<T>(
  builder: () => any,
  chunkSize = 1000,
): Promise<T[]> {
  const out: T[] = [];
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await builder().range(from, from + chunkSize - 1);
    if (error) throw error;
    const rows = (data ?? []) as T[];
    out.push(...rows);
    if (rows.length < chunkSize) break;
    from += chunkSize;
  }
  return out;
}

function csvEscape(v: unknown) {
  const s = v === null || v === undefined ? "" : String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

interface ModuleSession {
  id: string;
  semester: string;
  class: string;
  module_code: string;
  module_name: string | null;
  session_no: number;
  session_name: string;
  session_kind: string;
  active: boolean;
}

interface ModuleRow {
  module_code: string;
  module_name: string;
  class: string;
  semester: string;
}

const KINDS = ["lecture", "self_study", "presentation", "discussion", "custom"] as const;

export function SessionLibraryManager() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [rows, setRows] = useState<ModuleSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [semester, setSemester] = useState("");
  const [klass, setKlass] = useState("");
  const [moduleCode, setModuleCode] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [stats, setStats] = useState<{ total: number; backfilled: number; linked: number } | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  // search/filter state
  const [search, setSearch] = useState("");
  const [filterNo, setFilterNo] = useState("");
  const [filterKind, setFilterKind] = useState<string>("all");

  // form state
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ModuleSession | null>(null);
  const [fNo, setFNo] = useState("");
  const [fName, setFName] = useState("");
  const [fKind, setFKind] = useState<string>("lecture");
  const [fNoError, setFNoError] = useState<string | null>(null);

  // report filters
  const [reportSemester, setReportSemester] = useState<string>(ALL);
  const [reportModule, setReportModule] = useState<string>(ALL);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportSemesters, setReportSemesters] = useState<string[]>([]);
  const [reportModuleOpts, setReportModuleOpts] = useState<ModuleOpt[]>([]);

  async function loadModules() {
    const { data } = await supabase
      .from("modules")
      .select("module_code, module_name, class, semester")
      .order("module_code");
    setModules((data ?? []) as ModuleRow[]);
  }

  async function loadRows() {
    if (!semester || !klass || !moduleCode) {
      setRows([]);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("module_sessions")
      .select("*")
      .eq("semester", semester)
      .eq("class", klass)
      .eq("module_code", moduleCode)
      .order("session_no", { ascending: true });
    setLoading(false);
    if (error) {
      toast({ title: "Failed to load library", description: error.message, variant: "destructive" });
      return;
    }
    setRows((data ?? []) as ModuleSession[]);
  }

  async function loadStats() {
    setStatsLoading(true);
    const [totalRes, backfillRes, linkedRes] = await Promise.all([
      supabase.from("module_sessions").select("id", { count: "exact", head: true }),
      supabase.from("module_sessions").select("id", { count: "exact", head: true }).is("created_by", null),
      supabase.from("attendance_sessions").select("id", { count: "exact", head: true }).not("module_session_id", "is", null),
    ]);
    setStatsLoading(false);
    setStats({
      total: totalRes.count ?? 0,
      backfilled: backfillRes.count ?? 0,
      linked: linkedRes.count ?? 0,
    });
  }

  async function loadReportOptions() {
    const all = await fetchAllChunks<{ semester: string; module_code: string; module_name: string | null }>(
      () => supabase.from("module_sessions").select("semester, module_code, module_name"),
    );
    const sems = [...new Set(all.map((r) => r.semester).filter(Boolean))].sort();
    const modMap = new Map<string, ModuleOpt>();
    for (const r of all) {
      if (!modMap.has(r.module_code)) {
        modMap.set(r.module_code, { module_code: r.module_code, module_name: r.module_name });
      }
    }
    setReportSemesters(sems);
    setReportModuleOpts([...modMap.values()].sort((a, b) => a.module_code.localeCompare(b.module_code)));
  }

  async function handleDownloadReport() {
    setReportLoading(true);
    try {
      // 1. module_sessions, scoped
      let libQ: any = supabase.from("module_sessions").select("semester, class, module_code, module_name");
      if (reportSemester !== ALL) libQ = libQ.eq("semester", reportSemester);
      if (reportModule !== ALL) libQ = libQ.eq("module_code", reportModule);

      // 2. linked attendance_sessions (joined to module_sessions for semester)
      let linkedQ: any = supabase
        .from("attendance_sessions")
        .select("class, module_code, module_session_id, module_sessions!inner(semester, module_code, module_name, class)")
        .not("module_session_id", "is", null);
      if (reportSemester !== ALL) linkedQ = linkedQ.eq("module_sessions.semester", reportSemester);
      if (reportModule !== ALL) linkedQ = linkedQ.eq("module_sessions.module_code", reportModule);

      // 3. unmatched attendance_sessions (no module_session_id)
      let unmatchedQ: any = supabase
        .from("attendance_sessions")
        .select("id, class, module_code, topic, session_date, created_by")
        .is("module_session_id", null);
      if (reportModule !== ALL) unmatchedQ = unmatchedQ.eq("module_code", reportModule);

      const [libRows, linkedRows, unmatchedRows] = await Promise.all([
        fetchAllChunks<{ semester: string; class: string; module_code: string; module_name: string | null }>(() => libQ),
        fetchAllChunks<any>(() => linkedQ),
        fetchAllChunks<{ id: string; class: string | null; module_code: string | null; topic: string | null; session_date: string; created_by: string }>(() => unmatchedQ),
      ]);

      // Aggregate library counts by (sem|class|code)
      type Key = string;
      const keyOf = (sem: string, cls: string, code: string) => `${sem}||${cls}||${code}`;
      const libMap = new Map<Key, { semester: string; class: string; module_code: string; module_name: string | null; libCount: number }>();
      for (const r of libRows) {
        const k = keyOf(r.semester, r.class, r.module_code);
        const existing = libMap.get(k);
        if (existing) existing.libCount += 1;
        else libMap.set(k, { semester: r.semester, class: r.class, module_code: r.module_code, module_name: r.module_name, libCount: 1 });
      }

      // Aggregate linked counts using joined module_sessions semester
      const linkedMap = new Map<Key, number>();
      for (const r of linkedRows) {
        const ms = r.module_sessions;
        if (!ms) continue;
        const k = keyOf(ms.semester, r.class ?? ms.class, r.module_code ?? ms.module_code);
        linkedMap.set(k, (linkedMap.get(k) ?? 0) + 1);
        // ensure module_name is captured
        if (!libMap.has(k)) {
          libMap.set(k, {
            semester: ms.semester,
            class: r.class ?? ms.class,
            module_code: r.module_code ?? ms.module_code,
            module_name: ms.module_name,
            libCount: 0,
          });
        }
      }

      // Filter unmatched by reportSemester via libMap inference (best-effort)
      // Build a lookup of (class, module_code) -> semester(s) from library
      const semByClassCode = new Map<string, Set<string>>();
      for (const v of libMap.values()) {
        const k = `${v.class}||${v.module_code}`;
        if (!semByClassCode.has(k)) semByClassCode.set(k, new Set());
        semByClassCode.get(k)!.add(v.semester);
      }

      const unmatchedAgg = new Map<Key, number>();
      const unmatchedDetail: Array<{ semester: string; class: string; module_code: string; topic: string; session_date: string; created_by: string; id: string }> = [];

      for (const r of unmatchedRows) {
        const cls = r.class ?? "";
        const code = r.module_code ?? "";
        const sems = semByClassCode.get(`${cls}||${code}`);
        let sem = "(unknown)";
        if (sems && sems.size === 1) sem = [...sems][0];
        else if (sems && reportSemester !== ALL && sems.has(reportSemester)) sem = reportSemester;
        else if (sems && sems.size > 1) sem = [...sems].sort().join(" / ");

        // semester scoping for unmatched
        if (reportSemester !== ALL && sem !== reportSemester && sem !== "(unknown)") continue;

        const k = keyOf(sem, cls, code);
        unmatchedAgg.set(k, (unmatchedAgg.get(k) ?? 0) + 1);
        unmatchedDetail.push({
          semester: sem,
          class: cls,
          module_code: code,
          topic: r.topic ?? "",
          session_date: r.session_date,
          created_by: r.created_by,
          id: r.id,
        });
      }

      // Merge keys
      const allKeys = new Set<Key>([...libMap.keys(), ...linkedMap.keys(), ...unmatchedAgg.keys()]);

      const summaryRows = [...allKeys].map((k) => {
        const meta = libMap.get(k);
        const [sem, cls, code] = k.split("||");
        const lib = meta?.libCount ?? 0;
        const linked = linkedMap.get(k) ?? 0;
        const unmatched = unmatchedAgg.get(k) ?? 0;
        const total = linked + unmatched;
        const coverage = total > 0 ? (Math.round((linked / total) * 1000) / 10).toFixed(1) : "";
        return {
          semester: meta?.semester ?? sem,
          class: meta?.class ?? cls,
          module_code: meta?.module_code ?? code,
          module_name: meta?.module_name ?? "",
          lib, linked, unmatched, total, coverage,
        };
      }).sort((a, b) =>
        a.semester.localeCompare(b.semester) ||
        a.class.localeCompare(b.class) ||
        a.module_code.localeCompare(b.module_code)
      );

      // Build CSV
      const header = [
        "Semester", "Class", "Module Code", "Module Name",
        "Library Entries", "Linked Attendance Sessions",
        "Unmatched Attendance Sessions", "Total Attendance Sessions", "Coverage %",
      ];
      const lines: string[] = [];
      lines.push(header.map(csvEscape).join(","));
      for (const r of summaryRows) {
        lines.push([r.semester, r.class, r.module_code, r.module_name, r.lib, r.linked, r.unmatched, r.total, r.coverage].map(csvEscape).join(","));
      }

      if (unmatchedDetail.length > 0) {
        lines.push("");
        lines.push(csvEscape("--- Unmatched attendance sessions ---"));
        const detailHeader = ["Semester", "Class", "Module Code", "Topic", "Session Date", "Created By", "Session ID"];
        lines.push(detailHeader.map(csvEscape).join(","));
        for (const d of unmatchedDetail.sort((a, b) =>
          a.semester.localeCompare(b.semester) || a.class.localeCompare(b.class) ||
          a.module_code.localeCompare(b.module_code) || a.session_date.localeCompare(b.session_date)
        )) {
          lines.push([d.semester, d.class, d.module_code, d.topic, d.session_date, d.created_by, d.id].map(csvEscape).join(","));
        }
      }

      const today = new Date().toISOString().slice(0, 10);
      const semSlug = reportSemester === ALL ? "all" : reportSemester.replace(/\s+/g, "");
      const modSlug = reportModule === ALL ? "all" : reportModule.replace(/\s+/g, "");
      const filename = `session-library-backfill-${semSlug}-${modSlug}-${today}.csv`;

      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      toast({ title: "Report downloaded", description: `${summaryRows.length} group(s), ${unmatchedDetail.length} unmatched session(s).` });
    } catch (e: any) {
      toast({ title: "Report failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setReportLoading(false);
    }
  }

  useEffect(() => { loadModules(); loadStats(); loadReportOptions(); }, []);
  useEffect(() => { loadRows(); }, [semester, klass, moduleCode]);

  const semesters = useMemo(
    () => [...new Set(modules.map((m) => m.semester).filter(Boolean))].sort(),
    [modules]
  );
  const classes = useMemo(
    () => [...new Set(modules.filter((m) => m.semester === semester).map((m) => m.class).filter(Boolean))].sort(),
    [modules, semester]
  );
  const moduleOptions = useMemo(
    () => modules.filter((m) => m.semester === semester && m.class === klass),
    [modules, semester, klass]
  );

  const selectedModuleName = moduleOptions.find((m) => m.module_code === moduleCode)?.module_name ?? null;

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const noFilter = filterNo.trim();
    return rows.filter((r) => {
      if (q && !r.session_name.toLowerCase().includes(q)) return false;
      if (noFilter && String(r.session_no) !== noFilter) return false;
      if (filterKind !== "all" && r.session_kind !== filterKind) return false;
      return true;
    });
  }, [rows, search, filterNo, filterKind]);

  function openNew() {
    setEditing(null);
    const nextNo = rows.length ? Math.max(...rows.map((r) => r.session_no)) + 1 : 1;
    setFNo(String(nextNo));
    setFName("");
    setFKind("lecture");
    setFNoError(null);
    setFormOpen(true);
  }

  function openEdit(r: ModuleSession) {
    setEditing(r);
    setFNo(String(r.session_no));
    setFName(r.session_name);
    setFKind(r.session_kind);
    setFNoError(null);
    setFormOpen(true);
  }

  async function saveRow(e: React.FormEvent) {
    e.preventDefault();
    const no = Number(fNo);
    const name = fName.trim();
    if (!Number.isInteger(no) || no <= 0 || !name) {
      toast({ title: "Invalid input", description: "Session No must be a positive integer and Name is required.", variant: "destructive" });
      return;
    }
    // Pre-save duplicate check
    try {
      const dupes = await findDuplicateSessionNos(
        [{ semester, class: klass, module_code: moduleCode, session_no: no }],
        editing?.id,
      );
      if (dupes.has(sessionKeyOf(semester, klass, moduleCode, no))) {
        const msg = `Session No ${no} already exists for ${klass} · ${moduleCode} · Semester ${semester}. Pick a different number.`;
        setFNoError(msg);
        toast({ title: "Duplicate Session No", description: msg, variant: "destructive" });
        return;
      }
    } catch (err: any) {
      toast({ title: "Validation error", description: err.message ?? String(err), variant: "destructive" });
      return;
    }
    if (editing) {
      const { error } = await supabase
        .from("module_sessions")
        .update({ session_no: no, session_name: name, session_kind: fKind })
        .eq("id", editing.id);
      if (error) {
        toast({ title: "Update failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Session updated" });
    } else {
      const { error } = await supabase.from("module_sessions").insert({
        semester, class: klass, module_code: moduleCode, module_name: selectedModuleName,
        session_no: no, session_name: name, session_kind: fKind, created_by: user?.id,
      });
      if (error) {
        toast({ title: "Add failed", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Session added to library" });
    }
    setFormOpen(false);
    loadRows();
  }

  async function deleteRow(id: string) {
    const { error } = await supabase.from("module_sessions").delete().eq("id", id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Session removed from library" });
    loadRows();
  }

  return (
    <div className="space-y-4">
      <Card>
        <Collapsible open={summaryOpen} onOpenChange={setSummaryOpen}>
          <CardHeader className="pb-3">
            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-left">
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-4 w-4 text-primary" /> Backfill summary
              </CardTitle>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${summaryOpen ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent>
              <CardDescription className="mb-3">
                Snapshot of the migration that imported historical attendance topics into the Session Library.
              </CardDescription>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md border p-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Library className="h-3.5 w-3.5" /> Total library entries
                  </div>
                  <div className="mt-1 text-2xl font-semibold">{statsLoading ? "…" : (stats?.total ?? 0)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Database className="h-3.5 w-3.5" /> System-backfilled rows
                  </div>
                  <div className="mt-1 text-2xl font-semibold">{statsLoading ? "…" : (stats?.backfilled ?? 0)}</div>
                  <p className="text-[11px] text-muted-foreground mt-1">Rows created by the migration (no author).</p>
                </div>
                <div className="rounded-md border p-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Link2 className="h-3.5 w-3.5" /> Attendance sessions linked
                  </div>
                  <div className="mt-1 text-2xl font-semibold">{statsLoading ? "…" : (stats?.linked ?? 0)}</div>
                  <p className="text-[11px] text-muted-foreground mt-1">Past sessions now mapped to a library entry.</p>
                </div>
              </div>
              <div className="mt-4 border-t pt-3 space-y-2">
                <div className="text-xs font-medium text-muted-foreground">Coverage report</div>
                <div className="grid gap-2 sm:grid-cols-[1fr,1fr,auto] items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Semester</Label>
                    <Select value={reportSemester} onValueChange={setReportSemester}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>All semesters</SelectItem>
                        {reportSemesters.map((s) => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Module</Label>
                    <Select value={reportModule} onValueChange={setReportModule}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL}>All modules</SelectItem>
                        {reportModuleOpts.map((m) => (
                          <SelectItem key={m.module_code} value={m.module_code}>
                            {m.module_code}{m.module_name ? ` — ${m.module_name}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button size="sm" onClick={handleDownloadReport} disabled={reportLoading}>
                    <FileDown className="h-4 w-4 mr-1" />
                    {reportLoading ? "Building…" : "Download CSV"}
                  </Button>
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button size="sm" variant="ghost" onClick={loadStats} disabled={statsLoading}>
                  Refresh
                </Button>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5" /> Session Library</CardTitle>
              <CardDescription>
                Curriculum-wide catalogue of sessions per Semester → Class → Module. Teachers pick from this list to start attendance.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Cascading filters */}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>Semester</Label>
              <Select value={semester} onValueChange={(v) => { setSemester(v); setKlass(""); setModuleCode(""); }}>
                <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                <SelectContent>
                  {semesters.map((s) => <SelectItem key={s} value={s}>Semester {s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Class</Label>
              <Select value={klass} onValueChange={(v) => { setKlass(v); setModuleCode(""); }} disabled={!semester}>
                <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Module</Label>
              <Select value={moduleCode} onValueChange={setModuleCode} disabled={!klass}>
                <SelectTrigger><SelectValue placeholder="Select module" /></SelectTrigger>
                <SelectContent>
                  {moduleOptions.map((m) => (
                    <SelectItem key={m.module_code} value={m.module_code}>
                      {m.module_code} — {m.module_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {moduleCode && (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm text-muted-foreground">
                  Showing {filteredRows.length} of {rows.length} session{rows.length === 1 ? "" : "s"} for{" "}
                  <span className="font-medium text-foreground">{moduleCode}</span>
                  {selectedModuleName ? ` — ${selectedModuleName}` : ""} · {klass} · Sem {semester}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadSessionLibraryTemplate({ semester, class: klass, moduleCode, moduleName: selectedModuleName })}
                  >
                    <Download className="w-4 h-4 mr-1" /> Download template
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
                    <Upload className="w-4 h-4 mr-1" /> Bulk upload
                  </Button>
                  <Button size="sm" onClick={openNew}>
                    <Plus className="w-4 h-4 mr-1" /> Add session
                  </Button>
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-[1fr,140px,180px]">
                <Input
                  placeholder="Search by session name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Input
                  type="number"
                  min={1}
                  placeholder="Session No"
                  value={filterNo}
                  onChange={(e) => setFilterNo(e.target.value)}
                />
                <Select value={filterKind} onValueChange={setFilterKind}>
                  <SelectTrigger><SelectValue placeholder="All kinds" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All kinds</SelectItem>
                    {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(search || filterNo || filterKind !== "all") && (
                <div className="flex justify-end">
                  <Button size="sm" variant="ghost" onClick={() => { setSearch(""); setFilterNo(""); setFilterKind("all"); }}>
                    Clear filters
                  </Button>
                </div>
              )}

              <div className="border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">#</TableHead>
                      <TableHead>Session name</TableHead>
                      <TableHead className="w-32">Kind</TableHead>
                      <TableHead className="w-24 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRows.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs">{r.session_no}</TableCell>
                        <TableCell>{r.session_name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{r.session_kind}</Badge></TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost"><Trash2 className="h-3 w-3 text-destructive" /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove from library?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Past attendance records for this session are not affected. Teachers will no longer see "{r.session_name}" in the picker.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => deleteRow(r.id)}>Remove</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground text-sm py-8">
                          No sessions in the library yet for this module. Click "Add session" or "Bulk upload" to start.
                        </TableCell>
                      </TableRow>
                    )}
                    {!loading && rows.length > 0 && filteredRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground text-sm py-8">
                          No sessions match the current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {!moduleCode && (
            <div className="text-sm text-muted-foreground py-6 text-center">
              Select a Semester, Class, and Module to view or edit the session library.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit library session" : "Add library session"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveRow} className="space-y-3">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span><strong className="text-foreground">Session No</strong> must be unique per <strong className="text-foreground">Semester + Class + Module</strong>. The same number can be reused across different modules or semesters.</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Label>Session No</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger type="button" className="text-muted-foreground hover:text-foreground">
                        <Info className="h-3.5 w-3.5" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Must be unique within the same Semester, Class, and Module. Duplicates are blocked.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  type="number"
                  min={1}
                  value={fNo}
                  onChange={(e) => { setFNo(e.target.value); if (fNoError) setFNoError(null); }}
                  required
                  aria-invalid={!!fNoError}
                />
                {fNoError && <p className="text-destructive text-xs">{fNoError}</p>}
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Session name</Label>
                <Input value={fName} onChange={(e) => setFName(e.target.value)} required placeholder="e.g. Cardiovascular system" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Kind</Label>
              <Select value={fKind} onValueChange={setFKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button type="submit">{editing ? "Save changes" : "Add to library"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk upload — writes to module_sessions */}
      {moduleCode && (
        <SessionsBulkCreateDialog
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          context={{ semester, class: klass, moduleCode, moduleName: selectedModuleName ?? undefined }}
          mode="library"
          onCreated={loadRows}
        />
      )}
    </div>
  );
}
